/**
 * Realtime stream with reconnect + exponential backoff (§7).
 * - While the browser is retrying on its own (readyState CONNECTING) it sends Last-Event-ID itself; we let it, but
 *   after `maxBrowserRetries` failed attempts we take over so a flapping server is not hammered.
 * - When the stream is CLOSED (HTTP error, 401, deploy), we reconnect with backoff and pass `?since=<last id>`, since a new
 *   EventSource does not carry Last-Event-ID. The server replays from Postgres, so nothing is lost.
 * - Every reconnect after the first open fires `onReconnect` (the app invalidates what's on screen, belt and braces).
 */
import type { EventSourceLike } from "../api/client";
import { StreamEvent } from "../api/types";

export type StreamStatus = "connecting" | "open" | "reconnecting" | "stopped";

export type EventStreamOptions = {
  open: (since?: string) => EventSourceLike;
  onEvent: (e: StreamEvent) => void;
  onReconnect?: () => void;
  onStatus?: (s: StreamStatus) => void;
  baseDelayMs?: number;
  maxDelayMs?: number;
  maxBrowserRetries?: number;
  random?: () => number;
};

const CONNECTING = 0;
const CLOSED = 2;

export function backoffDelay(attempt: number, base: number, max: number, random: () => number): number {
  const exp = Math.min(max, base * 2 ** attempt);
  return Math.round(exp * (0.8 + 0.4 * random())); // ±20 % jitter so many tabs don't reconnect in lockstep
}

export class EventStream {
  private es: EventSourceLike | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private attempt = 0;
  private browserRetries = 0;
  private everOpened = false;
  private stopped = true;
  lastEventId: string | undefined;
  status: StreamStatus = "stopped";

  constructor(private readonly o: EventStreamOptions) {}

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    this.connect();
  }

  stop(): void {
    this.stopped = true;
    this.clearTimer();
    this.es?.close();
    this.es = null;
    this.setStatus("stopped");
  }

  /** Reconnect immediately (on `online` / tab becoming visible) if we are not connected. */
  nudge(): void {
    if (this.stopped || this.status === "open") return;
    this.clearTimer();
    this.es?.close();
    this.connect();
  }

  /** The delay the next scheduled reconnect will use (exposed for tests and the UI). */
  nextDelay(): number {
    return backoffDelay(this.attempt, this.o.baseDelayMs ?? 1000, this.o.maxDelayMs ?? 30_000, this.o.random ?? Math.random);
  }

  private connect(): void {
    this.setStatus(this.everOpened ? "reconnecting" : "connecting");
    const es = this.o.open(this.lastEventId);
    this.es = es;
    es.onopen = () => {
      if (this.es !== es) return;
      const wasReconnect = this.everOpened;
      this.everOpened = true;
      this.attempt = 0;
      this.browserRetries = 0;
      this.setStatus("open");
      if (wasReconnect) this.o.onReconnect?.();
    };
    es.onmessage = (ev: MessageEvent) => {
      if (this.es !== es) return;
      if (ev.lastEventId) this.lastEventId = ev.lastEventId;
      let raw: unknown;
      try { raw = JSON.parse(String(ev.data)); } catch { return; }
      const parsed = StreamEvent.safeParse(raw);
      if (parsed.success) this.o.onEvent(parsed.data);
    };
    es.onerror = () => {
      if (this.es !== es || this.stopped) return;
      if (es.readyState === CONNECTING && this.browserRetries < (this.o.maxBrowserRetries ?? 3)) {
        this.browserRetries++;
        this.setStatus("reconnecting");
        return; // the browser retries with Last-Event-ID
      }
      if (es.readyState !== CLOSED) es.close();
      this.es = null;
      this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    this.clearTimer();
    const delay = this.nextDelay();
    this.attempt++;
    this.setStatus("reconnecting");
    this.timer = setTimeout(() => { this.timer = null; if (!this.stopped) this.connect(); }, delay);
  }

  private clearTimer(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  private setStatus(s: StreamStatus): void {
    if (this.status === s) return;
    this.status = s;
    this.o.onStatus?.(s);
  }
}
